import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import proPlan from '../../../../assets/pro-plan.svg';
import currentProPlan from '../../../../assets/icon-pro.svg';
import enterprisePlan from '../../../../assets/icon-interprise-plan.svg';
import currentEnterprisePlan from '../../../../assets/icon-enterprise.svg';
import iconTeam from '../../../../assets/icon-team2.svg';
import iconBasicPlan from '../../../../assets/icon-plan.svg';
import confirmImg from '../../../../assets/images/confirm-gif.gif';
import { RxCheckCircled } from 'react-icons/rx';
import { FiCheckSquare } from 'react-icons/fi';
import { useContextHook } from 'use-context-hook';
import { AuthContext } from '../../../../Context/authContext';
import stripeService from '../../../../services/stripeService';
import Toast from '../../../molecules/Toast';
import Button from '../../../molecules/Button';
import { generatePricingData } from '../../../../helpers/common';
import Skeleton from '../../skeleton/Skeleton';
import { useStripe } from '@stripe/react-stripe-js';
import CenterModal from '../../../molecules/Modal/CenterModal';
import Loaders from '../../Loaders';
import { PricingStyles, StyledCol } from './PricingSection.styles';
import Billing from '../../Billing';
import ConfirmationBlock from '../../ConfirmationBlock';
import { DownloadRistrictionContext } from '../../../../Context/downloadRistriction';


const PricingSection = ({ onClose }) => {

  const { user, refetch, fetchUser, getPermissions } = useContextHook(AuthContext, v => ({
    user: v.user,
    refetch: v.refetch,
    fetchUser: v.fetchUser,
    getPermissions: v.getPermissions,
  }));
  const stripe = useStripe();
  const [loading, setLoading] = useState(false);
  const [planLoading, setPlanLoading] = useState(false);
  const [state, setState] = useState(null);
  const [monthlyPlanData, setMonthlyPlanData] = useState([]);
  const [yearlyPlanData, setYearlyPlanData] = useState([]);
  const fetched = useRef(false);
  const planTypeSet = useRef(false);
  const [unsubscribeLoading, setUnsubscribeLoading] = useState(false);
  const [paymentIntentState, setPaymentIntentState] = useState(null);
  const [openSubscriptionModal, setOpenSubscriptionModal] = useState(false);
  const [unsubscribePlan, setUnsubscribePlan] = useState(null);
  const [planType, setPlanType] = useState(user?.data?.subscriptionDetail?.interval === 'year' ? 'yearly' : 'monthly');
  const [activePlan, setActivePlan] = useState(null);
  const [showAllFeatures, setShowAllFeatures] = useState({}); // track per plan
  const [openBillingModal, setOpenBillingModal] = useState(false);
  const [selectedPlanForBilling, setSelectedPlanForBilling] = useState(null);
  const [unsubscribeClicked, setUnsubscribeClicked] = useState(false);
  const [openConfirmationModal, setOpenConfirmationModal] = useState(false);
  const { downloadRistrictionModal } = useContextHook(DownloadRistrictionContext, v => ({
    downloadRistrictionModal: v.downloadRistrictionModal,
  }));

  const getPlans = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await stripeService.getAllPlans();
      setMonthlyPlanData(resp?.data?.monthlyPlan || []);
      setYearlyPlanData(resp?.data?.yearlyPlan || []);
    } catch (error) {
      Toast({ type: 'error', message: error.message });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!fetched.current) {
      fetched.current = true;
      getPlans();
    }
  }, [getPlans]);


  useEffect(() => {
    if (!planTypeSet.current && user?.data?.subscriptionDetail?.interval) {
      setPlanType(user.data.subscriptionDetail.interval === 'year' ? 'yearly' : 'monthly');
      planTypeSet.current = true;
    }
  }, [user]);

  const activePlanId =
    user?.data?.subscriptionDetail?.status !== 'canceled' ? user?.data?.subscriptionDetail?.priceId : null;

  const usePricingData = ({ monthlyPlanData, yearlyPlanData, activePlanId }) => {
    return useMemo(
      () => ({
        monthly: generatePricingData({
          planData: monthlyPlanData,
          timeline: 'month',
          icon: proPlan,
          currentIcon: currentProPlan,
          activePlanId,
        }),
        yearly: generatePricingData({
          planData: yearlyPlanData,
          timeline: 'yearly',
          icon: enterprisePlan,
          currentIcon: currentEnterprisePlan,
          activePlanId,
        }),
      }),
      [monthlyPlanData, yearlyPlanData, activePlanId],
    );
  };

  const pricingData = usePricingData({ monthlyPlanData, yearlyPlanData, activePlanId });

  // Deduplicate plans based on ID
  const deduplicatedPricingData = useMemo(() => {
    const monthly = pricingData.monthly || [];
    const yearly = pricingData.yearly || [];

    const deduplicateArray = arr => {
      const seen = new Map();
      return arr.filter(plan => {
        if (!seen.has(plan.id)) {
          seen.set(plan.id, true);
          return true;
        }
        return false;
      });
    };

    return {
      monthly: deduplicateArray(monthly),
      yearly: deduplicateArray(yearly),
    };
  }, [pricingData]);


  useEffect(() => {
    setActivePlan(deduplicatedPricingData[planType]?.find(plan => plan.current) || null);
  }, [planType, deduplicatedPricingData]);

  const handlePlanClick = async plan => {
    setState(plan);
    setPlanLoading(true);
    try {
      const hasExistingSubscription = ['active', 'trialing'].includes(user?.data?.subscriptionDetail?.status);

      if (hasExistingSubscription) {
        const resp = await stripeService.updateSubscription({ newPriceId: plan.id });
        if (resp.success && resp.paymentStatus === 'succeeded') {
          Toast({ type: 'success', message: 'Subscription updated successfully.' });
          return;
        }

        if (resp.success && resp.requiresAction && resp.clientSecret) {
          if (!stripe) {
            Toast({ type: 'error', message: 'Stripe not initialized.' });
            return;
          }
          const { error, paymentIntent } = await stripe.confirmCardPayment(resp.clientSecret);
          setPaymentIntentState(paymentIntent);
          if (error) {
            Toast({ type: 'error', message: error.message || 'Payment confirmation failed.' });
            return;
          }
        }

        if (resp.requiresSetup) {
          Toast({
            type: 'info',
            message: resp.message || 'Please update your billing information to continue.',
          });
          return;
        }

        if (!resp.success) {
          Toast({
            type: 'error',
            message: resp.error || 'Payment failed. Please try another card.',
          });
        }
        return;
      }

      const appOrigin = process.env.REACT_APP_ORIGIN;
      const resp = await stripeService.createCheckoutSession({
        priceId: plan.id,
        successUrl: `${appOrigin}/dashboard`,
        cancelUrl: `${appOrigin}/dashboard`,
      });

      const checkoutUrl = resp?.data?.url;
      if (checkoutUrl) {
        window.location.assign(checkoutUrl);
        return;
      }

      throw new Error('Checkout URL is missing. Please try again.');
    } catch (error) {
      Toast({ type: 'error', message: error.message || 'Something went wrong.' });
    } finally {
      setState(null);
      setPlanLoading(false);
    }
  };

  const getPlanAction = plan => {
    if (plan.current) return 'Your Plan';
    if (!activePlan) return 'Upgrade Plan';

    switch (activePlan.title.trim()) {
      case 'Enterprise Plan':
        return 'Downgrade Plan';
      case 'Pro Plan':
        return plan.title.trim() === 'Enterprise Plan' ? 'Upgrade Plan' : 'Downgrade Plan';
      case 'Basic Plan':
        return 'Upgrade Plan';
      default:
        return 'Upgrade Plan';
    }
  };

  const handleUnsubscribe = async () => {
    setUnsubscribeClicked(true);
    setUnsubscribeLoading(true);
    try {
      let res = await stripeService.cancelSubscription();
      setUnsubscribePlan(res);
    } catch (error) {
      if (error?.requiresBillingUpdate) {
        Toast({
          type: 'info',
          message: error.message,
        });
        return;
      }
      Toast({ type: 'error', message: error.message });
    } finally {
      setUnsubscribeLoading(false);
    }
  };


  useEffect(() => {
    const handleNotification = async event => {
      const data = event.detail;
      if (data.actionType === 'subscription_updated' && data.title === 'Subscription Updated') {
        await getPermissions(true);
        setOpenSubscriptionModal(false);
        setOpenConfirmationModal(true);
      }
    };

    window.addEventListener('subscription-notification', handleNotification);
    return () => {
      window.removeEventListener('subscription-notification', handleNotification);
    };
  }, []);

  useEffect(() => {
    const handleNotification = event => {
      const data = event.detail;
      if (
        unsubscribeClicked &&
        data.actionType === 'subscription_canceled' &&
        data.title === 'Subscription Deleted'
      ) {
        fetchUser();
        setUnsubscribeClicked(false);
      }
    };

    window.addEventListener('subscription-notification', handleNotification);
    return () => {
      window.removeEventListener('subscription-notification', handleNotification);
    };
  }, [unsubscribeClicked, fetchUser]);

  const hasUnsubscribed = ['active', 'trialing'].includes(user?.data?.subscriptionDetail?.status);

  return (
    <>
      <CenterModal open={openSubscriptionModal} width="500" noHead isCloseAble={false}>
        <div className="leads-modal">
          {paymentIntentState?.status !== 'succeeded' && (
            <>
              <span className="heading">Your subscription is being updated...</span>
              <span className="subscribe-text">
                Hang tight — we’re just wrapping up the last step and finalizing your subscription details. It’ll only
                take a few seconds!
              </span>
            </>
          )}
          {paymentIntentState?.status === 'succeeded' && (
            <>
              <span className="heading subscribe-successfull">Subscription updated successfully.</span>

              <span className="subscribe-text">
                We’re just making sure everything is finalized. This may take a few moments.
              </span>
            </>
          )}
          <Loaders dotsLoader dotsBottom />
        </div>
      </CenterModal>
      <CenterModal open={openBillingModal} setOpen={setOpenBillingModal} title="Get your Membership" width="600">
        <Billing
          fromPricing={true}
          selectedPlan={selectedPlanForBilling}
          setOpenBillingModal={setOpenBillingModal}
          onCardSaveSuccess={() => {
            setOpenBillingModal(false);
            setOpenSubscriptionModal(false);
            setOpenConfirmationModal(true)
            // if (selectedPlanForBilling) {
            //   handlePlanClick(selectedPlanForBilling);
            // }
          }}
        />
      </CenterModal>
      <CenterModal width={600} open={openConfirmationModal} setOpen={setOpenConfirmationModal} noHead isCloseAble={false}>
        <ConfirmationBlock
          img={confirmImg}
          title="Subscription updated successfully!"
          btns={
            <Button onClick={() => setOpenConfirmationModal(false)}>
              Go Back
            </Button>
          }
        />
      </CenterModal>
      <PricingStyles>
        <div className="section-head">
          <div className="text-box">
            {downloadRistrictionModal && (
              <div className="download-restriction">
                <strong className="title">Download Ristriction!</strong>
                <span className="text">
                  You are not authorized to download lists. Please upgrade your plan to download lists.
                </span>
              </div>
            )}
            <strong className="title">Upgrade Plan</strong>
          </div>
          <div className="plans-header">
            <button className={`btn ${planType === 'yearly' ? 'active' : ''}`} onClick={() => setPlanType('yearly')}>
              Bill annually <span className="text">20% OFF</span>
            </button>
            <button className={`btn ${planType === 'monthly' ? 'active' : ''}`} onClick={() => setPlanType('monthly')}>
              Bill monthly
            </button>
          </div>
        </div>

        <div className="pricing-holder">
          {pricingData[planType]?.length ? (
            pricingData[planType].map((plan, index) => (
              <StyledCol key={index} data-aos={plan?.animation} $popularPlan={plan?.current}>
                <div className="header">
                  {plan.current && <span className="tag">Current Plan</span>}
                  <strong className="title">
                    <div className="icon-box">
                      <img
                        src={
                          plan.title === 'Basic Plan'
                            ? iconBasicPlan
                            : plan.title === 'Pro Plan'
                              ? currentProPlan
                              : plan.title === 'Enterprise Plan'
                                ? currentEnterprisePlan
                                : ''
                        }
                        alt={plan.title}
                      />
                    </div>
                    {plan.title}
                  </strong>
                  <div className="price-box">
                    <strong className="price">{plan.price}</strong>
                    <div className="wrap">
                      <span className="timeline">{planType === 'monthly' ? 'monthly' : 'yearly'} (USD)</span>
                      <span className="text">No Hidden Cost</span>
                    </div>
                  </div>
                  {/* <span className="description">{plan.description}</span> */}
                  <Button
                    rounded
                    loader={plan?.id === state?.id}
                    disabled={plan?.current || planLoading}
                    variant={plan?.current && 'white'}
                    type="button"
                    onClick={() => handlePlanClick(plan)}>
                    {getPlanAction(plan)}
                  </Button>
                </div>

                <ul className="info-list">
                  {(plan?.list || []).slice(0, showAllFeatures[plan.id] ? plan.list.length : 5).map((item, idx) => (
                    <li key={idx}>
                      <RxCheckCircled className="tick-mark" />
                      <span className="text">{item}</span>
                    </li>
                  ))}
                  {plan?.list?.length > 5 && !showAllFeatures[plan.id] && (
                    <li className="see-more">
                      <button
                        type="button"
                        onClick={() =>
                          setShowAllFeatures(prev => ({
                            ...prev,
                            [plan.id]: true,
                          }))
                        }>
                        See More
                      </button>
                    </li>
                  )}
                </ul>
              </StyledCol>
            ))
          ) : (
            <>
              <Skeleton width="100%" height="423" radius="33px" />
              <Skeleton width="100%" height="423" radius="33px" />
              <Skeleton width="100%" height="423" radius="33px" />
            </>
          )}
        </div>

        {/* {activePlanId && user?.data?.status === 'subscription_active' && ( */}
        {hasUnsubscribed && (
          <Button
            width="200px"
            variant="danger"
            onClick={handleUnsubscribe}
            loader={unsubscribeLoading}
            disabled={!activePlanId || unsubscribeLoading}>
            {user?.data?.subscriptionDetail?.status === 'active' ? 'Unsubscribe Plan' : 'Cancel Subscription'}
          </Button>
        )}
        {/* )} */}

        <div className="footer">
          <img className="team-img" src={iconTeam} width="25" alt="icon" />
          <span className="text">Need more detailed comparison of Plans?</span>
          <span className="text">
            Campare <span className="link">Houdiny AI Plans</span>
          </span>
        </div>
      </PricingStyles>
    </>
  );
};

export default PricingSection;
